import { Hoverable } from './Hoverable';
import { useMagnetic } from '../hooks/useMagnetic';
import { color, font } from '../styles/tokens';

// ---- Buttons -------------------------------------------------------------

// Solid accent pill (primary CTA). Magnetic on fine pointers.
export const PrimaryButton = ({ children, onClick, type = 'button', magnetic = true, style, ...rest }) => {
  const mag = useMagnetic();
  return (
    <Hoverable
      as="button"
      type={type}
      onClick={onClick}
      {...(magnetic ? mag : {})}
      style={{
        background: color.accent,
        color: color.bg,
        border: 'none',
        borderRadius: 999,
        padding: '17px 30px',
        font: `600 16px ${font.display}`,
        cursor: 'pointer',
        transition: 'transform .3s cubic-bezier(.16,1,.3,1),background .2s',
        boxShadow: '0 12px 40px -8px rgba(77,141,255,.65)',
        ...style,
      }}
      hoverStyle={{ background: color.accentSoft }}
      {...rest}
    >
      {children}
    </Hoverable>
  );
};

// Outlined / ghost pill (secondary CTA).
export const GhostButton = ({ children, onClick, magnetic = true, style, ...rest }) => {
  const mag = useMagnetic();
  return (
    <Hoverable
      as="button"
      onClick={onClick}
      {...(magnetic ? mag : {})}
      style={{
        background: 'rgba(255,255,255,.03)',
        color: color.text,
        border: '1px solid rgba(255,255,255,.16)',
        borderRadius: 999,
        padding: '17px 30px',
        font: `600 16px ${font.display}`,
        cursor: 'pointer',
        transition: 'transform .3s cubic-bezier(.16,1,.3,1),border-color .2s,color .2s',
        ...style,
      }}
      hoverStyle={{ borderColor: color.accent, color: color.accent }}
      {...rest}
    >
      {children}
    </Hoverable>
  );
};

// ---- Text bits -----------------------------------------------------------

// The bracketed mono eyebrow used above most section headings, e.g. [ ABOUT V-TECH ].
export const SectionLabel = ({ children, style }) => (
  <div
    style={{
      font: `500 12px ${font.mono}`,
      letterSpacing: '.16em',
      color: color.accent,
      ...style,
    }}
  >
    {children}
  </div>
);

// ---- Decorative --------------------------------------------------------

// Soft drifting radial glow used behind hero sections.
export const Blob = ({ style, animation = 'vt-drift1 16s ease-in-out infinite' }) => (
  <div
    aria-hidden="true"
    style={{
      position: 'absolute',
      borderRadius: '50%',
      filter: 'blur(70px)',
      pointerEvents: 'none',
      animation,
      ...style,
    }}
  />
);

// The hatched "photo pending" placeholder used wherever a real image isn't supplied.
export const Placeholder = ({ label, style, labelStyle }) => (
  <div
    style={{
      position: 'relative',
      background: 'repeating-linear-gradient(135deg,#151b26 0 15px,#10141d 15px 30px)',
      border: '1px solid rgba(255,255,255,.08)',
      overflow: 'hidden',
      ...style,
    }}
  >
    {label && (
      <span
        style={{
          position: 'absolute',
          left: 14,
          bottom: 14,
          font: `500 10px ${font.mono}`,
          textTransform: 'uppercase',
          color: color.textFaint,
          background: 'rgba(10,12,18,.85)',
          border: '1px solid rgba(255,255,255,.08)',
          padding: '5px 9px',
          borderRadius: 7,
          ...labelStyle,
        }}
      >
        {label}
      </span>
    )}
  </div>
);
